import { Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { useAuth, SignedIn, SignedOut } from "@clerk/react-router";
import { setMetaDescription, setCanonicalUrl, setOpenGraphTags, setTwitterCardTags } from "@/lib/seo";
import { 
  generateOrganizationSchema, 
  generateWebSiteSchema, 
  generateSoftwareApplicationSchema,
  addSchemaToPage 
} from "@/lib/schema";
import DashboardPage from "./pages/Dashboard";
import IncomePage from "./pages/Income";
import ExpensesPage from "./pages/Expenses";
import SavingsPage from "./pages/Savings";
import SignupPage from "./pages/Signup";
import SigninPage from "./pages/Signin";
import SsoCallbackPage from "./pages/SsoCallback";
import SettingsPage from "./pages/Settings";
import AboutPage from "./pages/About";
import MethodPage from "./pages/Method";
import TermsPage from "./pages/Terms";
import SecurityPage from "./pages/Security";
import Layout from "./components/Layout";
import PublicLayout from "./components/PublicLayout";
import { SettingsProvider } from "./contexts/SettingsContext";
import { TooltipProvider } from "./components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Logo } from "./components/Logo";
import Footer from "./components/Footer";
import "./index.css";

function Home() {
  useEffect(() => {
    const title = "Free Budgeting Tool - Munthly";
    const description = "Munthly is a free budgeting tool that helps you take control of your finances. Track income, expenses, and savings with the Munthly Method for a stable monthly balance sheet.";
    
    document.title = title;
    setMetaDescription(description);
    const canonicalCleanup = setCanonicalUrl("/");
    const ogCleanup = setOpenGraphTags(title, description, "/");
    const twitterCleanup = setTwitterCardTags(title, description);
    
    // Add schema for home page
    const organizationSchema = generateOrganizationSchema();
    const webSiteSchema = generateWebSiteSchema();
    const softwareAppSchema = generateSoftwareApplicationSchema();
    
    const schemaCleanup = addSchemaToPage([organizationSchema, webSiteSchema, softwareAppSchema]);
    
    return () => {
      canonicalCleanup();
      ogCleanup();
      twitterCleanup();
      schemaCleanup();
    };
  }, []);

  const { isSignedIn } = useAuth();
  if (isSignedIn) return <Navigate to="/dashboard" />;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="text-center space-y-8 max-w-md">
          <div className="flex items-center justify-center">
            <Logo variant="full" className="h-20 w-auto max-w-full" />
          </div>
          <div className="space-y-2">
            <h1 className="text-lg text-muted-foreground">A free budgeting tool that helps you take control of your finances</h1>
          </div>
          <div className="flex flex-col gap-4 items-center justify-center pt-4">
            <Link to="/signup">
              <Button size="lg" className="w-full sm:w-auto min-w-[200px]">Get Started</Button>
            </Link>
            <div className="flex gap-2 text-sm items-center">
              <span className="text-muted-foreground">Already use Munthly?</span>
              <Link to="/signin" className="text-primary hover:underline font-medium">Sign In</Link>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth();
  const location = useLocation();

  // Show loading state while Clerk is initializing
  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-2">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/signin" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}

export function App() {
  return (
    <SettingsProvider>
      <TooltipProvider delayDuration={0}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/signup/*" element={<SignupPage />} />
          <Route path="/signin/*" element={<SigninPage />} />
          <Route path="/signin/sso-callback" element={<SsoCallbackPage />} />
          <Route path="/sso-callback" element={<SsoCallbackPage />} />

          {/* Public Footer Pages */}
          <Route element={<PublicLayout />}>
            <Route path="/about" element={<AboutPage />} />
            <Route path="/method" element={<MethodPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/security" element={<SecurityPage />} />
          </Route>

          {/* Protected Routes wrapped in Layout */}
          <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/income" element={<IncomePage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/savings" element={<SavingsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Route>

        </Routes>
      </TooltipProvider>
    </SettingsProvider>
  );
}

export default App;
